import { emit } from './client.js';
import { newTrace } from './trace.js';

let lastNavAt = null;
let lastTemplate = null;
let navStartAt = null;    // beforeEach stamp → nav_ms (this route's resolve time)
let currentLoadMs = null; // carried until the route is left; emitted as nav_ms on departure
let hiddenMs = 0;         // accumulated hidden ms since last nav
let hiddenSince = null;   // timestamp when tab went hidden (null = currently visible)
let registered = false;
let removeAfter = null;   // Vue Router afterEach() remover
let removeBefore = null;  // Vue Router beforeEach() remover

/** The route TEMPLATE the user is currently on (for non-nav events). */
export function currentRouteTemplate() { return lastTemplate; }

/** The route TEMPLATE (e.g. /admin/clients/:id), never the filled path. */
export function routeTemplate(to) {
  const matched = to.matched && to.matched[to.matched.length - 1];
  const tmpl = (matched && matched.path) || '';
  return tmpl.replace(/(:[A-Za-z_][A-Za-z0-9_]*)\?/g, '$1'); // drop optional "?"
}

function onVisibility() {
  try {
    const now = Date.now();
    if (document.visibilityState === 'hidden') {
      hiddenSince = now;
    } else if (hiddenSince != null) {
      hiddenMs += now - hiddenSince;
      hiddenSince = null;
    }
  } catch { /* telemetry must never break navigation */ }
}

function emitFinalRouteView() {
  try {
    const now = Date.now();
    // flush any in-progress hidden interval
    if (hiddenSince != null) { hiddenMs += now - hiddenSince; hiddenSince = null; }
    if (lastTemplate != null && lastNavAt != null) {
      const dwell = now - lastNavAt;
      emit({
        event_type: 'nav', name: 'route_view', route_template: lastTemplate,
        props: {
          dwell_ms: dwell,
          active_ms: Math.max(0, dwell - hiddenMs),
          nav_ms: currentLoadMs,
          final: true,
        },
      });
      // guard against bfcache-resume double-count: stamp lastNavAt and also
      // reset the hidden/load accumulators so a bfcache restore + subsequent
      // real nav cannot emit a second route_view carrying stale hiddenMs.
      lastNavAt = now;
      hiddenMs = 0;
      hiddenSince = null;
      currentLoadMs = null;
      navStartAt = null;
    }
  } catch { /* telemetry must never break navigation */ }
}

export function registerNavTracking(router) {
  if (registered) return;
  registered = true;
  // Seed the current route so pre-first-nav events carry a template too.
  try {
    const cur = router.currentRoute && router.currentRoute.value;
    if (cur) lastTemplate = routeTemplate(cur);
  } catch { /* best-effort; afterEach corrects on first nav */ }

  // Stamp the start of every navigation so we can compute resolve latency.
  removeBefore = router.beforeEach(() => {
    try { navStartAt = Date.now(); } catch { /* best-effort */ }
  });

  removeAfter = router.afterEach((to, from, failure) => {
    try {
      const now = Date.now();
      // flush any in-progress hidden interval
      if (hiddenSince != null) { hiddenMs += now - hiddenSince; hiddenSince = null; }

      if (failure) {
        // user stayed put — emit a distinct signal, do NOT rotate dwell state
        emit({
          event_type: 'nav', name: 'nav_failed', route_template: lastTemplate,
          props: { to_template: routeTemplate(to), failure_type: failure.type },
        });
        navStartAt = null;
        // re-arm the hidden clock: the tab may still be hidden after the failed nav
        if (document.visibilityState === 'hidden') hiddenSince = now;
        return;
      }

      // emit the previous route's dwell before rotating
      if (lastTemplate != null && lastNavAt != null) {
        const dwell = now - lastNavAt;
        emit({
          event_type: 'nav', name: 'route_view', route_template: lastTemplate,
          props: {
            dwell_ms: dwell,
            active_ms: Math.max(0, dwell - hiddenMs),
            to_template: routeTemplate(to),
            nav_ms: currentLoadMs,
          },
        });
      }

      // rotate state for the new route
      lastNavAt = now;
      lastTemplate = routeTemplate(to);
      currentLoadMs = navStartAt != null ? now - navStartAt : null;
      navStartAt = null;
      hiddenMs = 0;
      // re-arm the hidden clock: the tab may still be hidden after the nav
      if (document.visibilityState === 'hidden') hiddenSince = now;
      newTrace(); // rotate the correlation trace per navigation
    } catch {
      /* telemetry must never break navigation */
    }
  });

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', emitFinalRouteView);
}

/** Remove both router hooks + both DOM listeners, reset all state (HMR teardown / tests). */
export function stopNavTracking() {
  if (typeof removeBefore === 'function') removeBefore();
  if (typeof removeAfter === 'function') removeAfter();
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('pagehide', emitFinalRouteView);
  removeBefore = null;
  removeAfter = null;
  registered = false;
  lastNavAt = null;
  lastTemplate = null;
  navStartAt = null;
  currentLoadMs = null;
  hiddenMs = 0;
  hiddenSince = null;
}

export function _resetForTest() { stopNavTracking(); }
